import { api, getHeaders } from '@/api';
import type { CodeString } from '@/consts/codes';
import { getUserId } from '@/stores/useAuthStore';
import type { MutationOptions } from '@tanstack/react-query';

import { fetchSettings, SettingCategory } from './queries';
import type { Setting } from './schema';
import { extractFirstSetting, parseSettingsData } from './utils';

const MAX_RECENT_ITEMS = 5;

interface UpsertSettingBody {
  Id: string;
  Category: string;
  UserId: string;
  Data: string;
  RecordVersion: number;
}

const buildSettingForUpsert = (
  existing: Setting | undefined,
  category: SettingCategory,
  menuItems: { Code: string }[]
): UpsertSettingBody => {
  const userId = getUserId() ?? '';
  return {
    Id: existing?.Id ?? crypto.randomUUID(),
    Category: category,
    UserId: userId,
    Data: JSON.stringify({ MenuItems: menuItems }),
    RecordVersion: existing?.RecordVersion ?? 0
  };
};

const upsertSetting = async (setting: UpsertSettingBody): Promise<void> => {
  await api.post('md/api/Settings/upsert', setting, getHeaders());
};

export const settingsMutationEndpoints = {
  add: async (category: SettingCategory, code: CodeString): Promise<void> => {
    if (category === SettingCategory.GridSaveLayout) {
      throw new Error('GridSaveLayout not supported for add mutation');
    }

    const parsed = await fetchSettings(category);
    const existing = extractFirstSetting(parsed);
    const currentCodes = existing?.Data ? parseSettingsData(existing.Data) : [];

    if (category === SettingCategory.Favorites) {
      const alreadyExists = currentCodes.some((c) => c.toLowerCase() === code.toLowerCase());
      if (alreadyExists) return;
      const menuItems = [...currentCodes.map((c) => ({ Code: c })), { Code: code }];
      await upsertSetting(buildSettingForUpsert(existing, category, menuItems));
    } else {
      // Recents: move-to-front + truncate to max 5
      const filtered = currentCodes.filter((c) => c.toLowerCase() !== code.toLowerCase());
      const menuItems = [{ Code: code }, ...filtered.map((c) => ({ Code: c }))].slice(0, MAX_RECENT_ITEMS);
      await upsertSetting(buildSettingForUpsert(existing, category, menuItems));
    }
  },

  remove: async (category: SettingCategory, code: CodeString): Promise<void> => {
    if (category === SettingCategory.GridSaveLayout) {
      throw new Error('GridSaveLayout not supported for remove mutation');
    }

    const parsed = await fetchSettings(category);
    const existing = extractFirstSetting(parsed);
    const currentCodes = existing?.Data ? parseSettingsData(existing.Data) : [];
    const menuItems = currentCodes.filter((c) => c.toLowerCase() !== code.toLowerCase()).map((c) => ({ Code: c }));
    await upsertSetting(buildSettingForUpsert(existing, category, menuItems));
  }
};

export const settingsMutations = {
  add: (category: SettingCategory, code: CodeString): MutationOptions<void> => ({
    mutationKey: ['settings', category, 'add', code],
    mutationFn: () => settingsMutationEndpoints.add(category, code)
  }),
  remove: (category: SettingCategory, code: CodeString): MutationOptions<void> => ({
    mutationKey: ['settings', category, 'remove', code],
    mutationFn: () => settingsMutationEndpoints.remove(category, code)
  })
};
